import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getResultByCode, processResultByCode, payResultByCode } from '../api/results';
import { PublicResult, WalletOnePaymentResponse } from '../api/types';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import Footer from '../components/common/Footer';
import AddressText from '../components/common/AddressText';
import { useLang } from '../context/LangContext';
import { MAX_PUBLIC_DOWNLOADS } from '../constants';
import { downloadBlob } from '../utils/download';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Box = styled(Card)`
  width: 100%;
  max-width: 560px;
`;

const Row = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin: 12px 0;
  text-align: center;
`;

const Notice = styled.p<{ $kind?: 'error' | 'info' | 'success' }>`
  color: ${({ $kind, theme }) =>
    $kind === 'error' ? '#e32636'
    : $kind === 'success' ? '#44944a'
    : theme.colors.info};
  margin: 12px 0;
  text-align: center;
`;

const StatusInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.md} 0;
  text-align: center;
`;

/* ── Генерация: пульсирующие стрелки и таймер ── */
const GeneratingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 12px 0;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.95rem;
`;

const PulseArrow = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.3rem;
  animation: pulseArrow 1.2s ease-in-out infinite;

  @keyframes pulseArrow {
    0%, 100% { opacity: 0.35; transform: translateX(0); }
    50% { opacity: 1; transform: translateX(4px); }
  }
`;

const formatElapsed = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

/* ── Генерация: пульсирующие стрелки и таймер ── */

const PublicResultPage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState<PublicResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Зелёное пояснение (например, «доступно после оплаты») — отдельно от ошибок.
  const [payInfo, setPayInfo] = useState('');
  // Публичный запуск генерации: индикатор, ошибка, кнопка повторной попытки.
  const [processLoading, setProcessLoading] = useState(false);
  const [processErrorText, setProcessErrorText] = useState('');
  const [retryDeadline, setRetryDeadline] = useState(0);
  const [now, setNow] = useState(Date.now());
  const processStartedRef = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (!code) return;
    // silent — фоновое автообновление по таймеру, без показа «Загрузка…»
    if (!silent) setLoading(true);
    setError('');
    try {
      const result = await getResultByCode(code);
      setData(result);
      setRetryDeadline(result.generation_retry_after && result.generation_retry_after > 0 ? Date.now() + result.generation_retry_after * 1000 : 0);
    } catch (loadError) {
      // 404 — кода нет в базе; остальные случаи (сеть, 5xx) — общая ошибка загрузки.
      const status = (loadError as { response?: { status?: number } } | null)?.response?.status;
      setError(status === 404 ? t('resultCodeNotFound') : t('resultLoadError'));
    } finally {
      setLoading(false);
    }
  }, [code, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const payment = new URLSearchParams(location.search).get('payment');
    if (payment === 'success') setNotice(t('resultPaymentSuccess'));
    if (payment === 'fail') setNotice(t('resultPaymentFail'));
    if (payment === 'success' || payment === 'fail') void load();
  }, [location.search, load, t]);

  // Автообновление: пока идёт генерация (processing), опрашиваем сервер каждые
  // 15 секунд, чтобы кнопка и статус обновились сами, без F5.
  useEffect(() => {
    if (data?.status !== 'processing') return;
    const timer = window.setInterval(() => { void load(true); }, 15000);
    return () => window.clearInterval(timer);
  }, [data?.status, load]);

  // Один раз на код: запускаем генерацию заключения (если она ещё не запущена).
  // Публичный запуск генерации заключения по коду сессии.
  const triggerProcess = useCallback(async () => {
    if (!code) return;
    setProcessLoading(true);
    setProcessErrorText('');
    try {
      const res = await processResultByCode(code);
      setRetryDeadline(res.retry_after && res.retry_after > 0 ? Date.now() + res.retry_after * 1000 : 0);
      if (res.status === 'redirect') {
        // ВРЕМЕННО: незарегистрированный пользователь направляется на внешний
        // сайт (psydecrypt.online) вместо отправки первички в LLM.
        if (res.redirect_url) {
          window.location.href = res.redirect_url;
          return;
        }
        setProcessErrorText(t('resultGenerationServiceError'));
      } else if (res.status === 'error') {
        setProcessErrorText(res.generation_error || t('resultGenerationServiceError'));
      } else if (res.status === 'blocked') {
        setProcessErrorText(t('resultAttemptsExceeded'));
      } else if (res.status === 'processing' || res.status === 'ready') {
        setProcessErrorText('');
      }
      await load(true);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400 || status === 404 || status === 429) {
        setProcessErrorText(detail || t('resultProcessError'));
      } else {
        setProcessErrorText(t('resultGenerationServiceError'));
      }
      await load(true);
    } finally {
      setProcessLoading(false);
    }
  }, [code, load, t]);

  // Один раз на код: запускаем генерацию заключения (если она ещё не запущена).
  useEffect(() => {
    if (!code || processStartedRef.current) return;
    processStartedRef.current = true;
    void triggerProcess();
  }, [code, triggerProcess]);

  // Тикающий таймер для индикатора генерации: работает только пока идёт
  // генерация, иначе страница не перерисовывается каждую секунду.
  useEffect(() => {
    if (data?.status !== 'processing') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [data?.status]);

  const submitPayment = (payment: WalletOnePaymentResponse) => {
    if (payment.confirmation_url) {
      // ЮKassa: открываем виджет подтверждения в новой вкладке.
      setNotice(t('resultPaymentRedirect'));
      window.open(payment.confirmation_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!payment.action) {
      setError(t('resultPaymentUnavailable'));
      return;
    }

    // Режим-заглушка (redirect): внешняя страница (Сбер) открывается в НОВОЙ вкладке.
    if (!payment.fields || Object.keys(payment.fields).length === 0) {
      setNotice(t('resultPaymentRedirect'));
      window.open(payment.action, '_blank', 'noopener,noreferrer');
      return;
    }

    const form = document.createElement('form');
    form.method = payment.method || 'POST';
    form.action = payment.action;
    form.target = '_blank';
    form.setAttribute('aria-label', t('resultPaymentFormLabel'));
    form.style.position = 'absolute';
    form.style.left = '-10000px';
    Object.entries(payment.fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    setNotice(t('resultPaymentRedirect'));
    window.setTimeout(() => form.submit(), 0);
  };

  const paymentErrorMessage = (paymentError: unknown): string => {
    const status = (paymentError as { response?: { status?: number } })?.response?.status;
    if (status === 503) return t('resultPaymentNotConfigured');
    if (status === 404) return t('resultPaymentResultNotFound');
    return t('resultPaymentError');
  };

  const handlePay = async (event: FormEvent) => {
    event.preventDefault();
    setPaying(true);
    setError('');
    setNotice('');
    setPayInfo('');
    try {
      // Платёжный эндпоинт возвращает action + fields для HTML POST-формы Wallet One.
      const payment = await payResultByCode(code);
      // TEMP (тест): при PAYMENT_TEST_MOCK заказ уже помечен оплаченным, но
      // внешнюю страницу (Сбер) всё равно открываем в НОВОЙ вкладке.
      if (payment.status === 'paid') {
        submitPayment(payment);
        await load();
      } else {
        submitPayment(payment);
      }
    } catch (paymentError) {
      setError(paymentErrorMessage(paymentError));
    } finally {
      setPaying(false);
    }
  };

  const handleDownload = async () => {
    setError('');
    setPayInfo('');
    try {
      // Cache-buster: гарантия, что fetch не отдаст закэшированный PDF,
      // а каждый клик реально дошёл до сервера и увеличил счётчик.
      const response = await fetch(`/api/results/by-code/${encodeURIComponent(code)}/pdf?t=${Date.now()}`);
      if (response.status === 403) {
        // Различаем причину отказа сервера: не оплачено либо лимит скачиваний исчерпан.
        let detail = '';
        try { detail = (await response.json())?.detail ?? ''; } catch { /* тело не JSON */ }
        throw new Error(detail === 'download_limit_reached'
          ? t('resultDownloadLimitReached').replace('{times}', String(data?.download_limit ?? MAX_PUBLIC_DOWNLOADS))
          : t('resultDownloadRequiresPayment'));
      }
      if (!response.ok) throw new Error(t('resultPdfDownloadError'));
      const blob = await response.blob();
      downloadBlob(blob, `conclusion-${code}.pdf`);
      // После скачивания возвращаем пользователя на страницу ввода кода:
      // повторно скачать можно, введя код заново (пока лимит не исчерпан).
      navigate('/results-gates');
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : t('resultPdfDownloadError'));
    }
  };

  // Клик по «Скачать»: заключение есть, но без оплаты или после исчерпания лимита
  // вместо файла показываем поясняющее сообщение.
  const handleDownloadClick = () => {
    if (!data) return;
    const paid = data.paid || data.allow_unpaid;
    if (!paid) { setError(''); setPayInfo(t('resultDownloadRequiresPayment')); return; }
    const used = data.conclusion_public_downloads ?? 0;
    const limit = data.download_limit ?? MAX_PUBLIC_DOWNLOADS;
    if (used >= limit) { setError(t('resultDownloadLimitReached').replace('{times}', String(limit))); return; }
    void handleDownload();
  };

  // Цена без дробных нулей: "400.00" → "400".
  const price = data?.payment_amount ? String(parseFloat(data.payment_amount)) : '—';
  const currency = data?.payment_currency || t('resultCurrency');

  // Время с начала генерации (для таймера) и остаток до повторной попытки.
  const genStartedAt = data?.generation_started_at ? new Date(data.generation_started_at).getTime() : null;
  const genElapsed = genStartedAt ? Math.max(0, Math.floor((now - genStartedAt) / 1000)) : 0;
  const retryRemain = retryDeadline > 0 ? Math.max(0, Math.ceil((retryDeadline - now) / 1000)) : 0;

  // Кнопка «Заключение ещё не создано» — только когда в базе нет заключения по коду.
  const showConclusionMissing = Boolean(data && !data.conclusion_exists);
  // Заключение есть в базе → видима либо кнопка оплаты (не оплачено), либо кнопка
  // скачивания (оплачено, лимит не исчерпан), либо Notice об исчерпании лимита.
  const conclusionExists = Boolean(data?.conclusion_exists);

  // Сколько публичных скачиваний осталось (реципрокно счётчику в кнопке «Забрать…»).
  const publicLeft = Math.max(0, (data?.download_limit ?? MAX_PUBLIC_DOWNLOADS) - (data?.conclusion_public_downloads ?? 0));
  const downloadLabel = t('resultDownloadButton').replace('{n}', String(publicLeft));
  // Блок над кнопками: при исчерпанном лимите не показываем «доступны для скачивания» —
  // ниже будет Notice с текстом про обращение к администрации.
  const statusInfo = data
    ? ((data.generation_status === 'error' || data.generation_status === 'blocked' || processErrorText)
        ? ''
        : (data.status === 'results' && publicLeft <= 0
            ? ''
            : {
                processing: t('resultProcessingInfo'),
                awaiting_payment: t('resultAwaitingPaymentInfo'),
                results: t('resultResultsInfo'),
              }[data.status]))
    : '';
  // Строка «Статус»: не оплачено — штатный статус; оплачено и есть остаток —
  // «Оплачено. Доступно (N) раза»; лимит исчерпан — «Достигнут лимит скачиваний».
  // Если админ разрешил скачивание без оплаты — статус как у оплаченного.
  let statusText = data ? t(`resultStatus_${data.status}` as 'resultStatus_processing') : '';
  // При ошибке/блокировке генерации не показываем «Результаты» — статусная строка
  // отражает отклонение, а подробности и кнопка повтора выводятся отдельным блоком.
  if (data?.generation_status === 'error' || data?.generation_status === 'blocked') {
    statusText = t('resultStatusRejected');
  } else if (processErrorText) {
    statusText = t('resultStatusRejected');
  }
  const effectivePaid = data?.paid || data?.allow_unpaid;
  if (effectivePaid) {
    if (publicLeft <= 0) {
      statusText = t('resultStatusLimitReached');
    } else if (data.pdf_ready) {
      statusText = publicLeft === 1
        ? t('resultStatusPaidAvailableOne')
        : t('resultStatusPaidAvailableFew').replace('{n}', String(publicLeft));
    }
  }

  return (
    <Page>
      <Main>
        <Box>
        <h1 style={{ textAlign: 'center' }}>{t('resultPageTitle')}</h1>
        {loading && <Row>{t('genLoading')}</Row>}
        {error && <Notice $kind="error"><AddressText text={error} /></Notice>}
        {payInfo && <Notice $kind="success">{payInfo}</Notice>}
        {/* Сообщение о невозможности запуска (400/заполненность/429/404):
            показываем вне блока generation_status=error, т.к. при отказе статус остаётся idle. */}
        {processErrorText && !error && data?.generation_status !== 'error' && data?.generation_status !== 'blocked' && (
          <Notice $kind="error">{processErrorText}</Notice>
        )}
        {error && !data && (
          <p style={{ marginBottom: 16, textAlign: 'center' }}>
            <Button variant="outline" size="md" onClick={() => navigate('/results-gates')}>
              {t('resultBackToCode')}
            </Button>
          </p>
        )}
        {notice && <Notice>{notice}</Notice>}
        {!loading && data && (
          <>
            <Row><strong>{t('testTitle')}:</strong> {data.test_title || '—'}</Row>
            <Row><strong>{t('resultCodeLabel')}:</strong> {data.share_code || code}</Row>
            <Row><strong>{t('resultStatusLabel')}:</strong> {statusText}</Row>
            <Row><strong>{t('resultPaidLabel')}:</strong> {data.paid ? t('publishedYes') : t('publishedNo')}</Row>
            <Row><strong>{t('resultPriceLabel')}:</strong> {price} {currency}</Row>
            <StatusInfo>{statusInfo}</StatusInfo>
            {data.generation_status === 'processing' && (
              <>
                <GeneratingIndicator>
                  <PulseArrow>⇄</PulseArrow>
                  <span>{t('resultGeneratingInfo').replace('{s}', formatElapsed(genElapsed))}</span>
                </GeneratingIndicator>
                {(data.queue_position ?? 0) > 0 && (
                  <Notice $kind="info">
                    {t('resultQueuedInfo').replace('{n}', String(data.queue_position ?? 0))}
                  </Notice>
                )}
                <Notice $kind="info">{t('resultProcessingWait')}</Notice>
              </>
            )}
            {data.generation_status === 'idle' && data.status === 'processing' && (
              <>
                <GeneratingIndicator>
                  <PulseArrow>⇄</PulseArrow>
                  <span>{t('resultGeneratingInfo').replace('{s}', formatElapsed(genElapsed))}</span>
                </GeneratingIndicator>
                <Notice $kind="info">{t('resultProcessingWait')}</Notice>
              </>
            )}
            {data.generation_status === 'error' && (
              <>
                <Notice $kind="error">{processErrorText || data.generation_error || t('resultGenerationServiceError')}</Notice>
                {retryRemain > 0 ? (
                  <Notice $kind="info">{t('resultRetryAfter').replace('{n}', String(retryRemain))}</Notice>
                ) : (
                  <p style={{ marginTop: 12, textAlign: 'center' }}>
                    <Button variant="outline" size="md" onClick={() => void triggerProcess()} disabled={processLoading}>
                      {processLoading ? t('genLoading') : t('resultRetryButton')}
                    </Button>
                  </p>
                )}
              </>
            )}
            {data.generation_status === 'blocked' && (
              <Notice $kind="error">{t('resultAttemptsExceeded')}</Notice>
            )}
            {showConclusionMissing && <Row>{t('resultConclusionInfo')}</Row>}
            {showConclusionMissing && (
              <p style={{ marginTop: 20, textAlign: 'center' }}>
                <Button variant="primary" size="md" disabled>
                  {t('resultDownloadUnavailable')}
                </Button>
              </p>
            )}
            {conclusionExists && !effectivePaid && data.pdf_ready && (
              <form onSubmit={handlePay} style={{ marginTop: 20, textAlign: 'center' }}>
                <Button type="submit" variant="primary" size="lg" disabled={paying} fullWidth>
                  {paying ? t('resultPaymentLoading') : t('resultWalletOnePayment')}
                </Button>
              </form>
            )}
            {conclusionExists && effectivePaid && data.pdf_ready && publicLeft > 0 && (
              <p style={{ marginTop: 20, textAlign: 'center' }}>
                <Button variant="primary" size="md" onClick={handleDownloadClick}>
                  {downloadLabel}
                </Button>
              </p>
            )}
            {conclusionExists && effectivePaid && publicLeft <= 0 && (
              <Notice $kind="info">{t('resultDownloadLimitReached').replace('{times}', String(data?.download_limit ?? MAX_PUBLIC_DOWNLOADS))}</Notice>
            )}
          </>
        )}
        </Box>
      </Main>
      <Footer />
    </Page>
  );
};

export default PublicResultPage;
